import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, message, Row, Col, Select, Space, Typography,
  Spin, Switch, Divider, Tooltip, Popconfirm
} from 'antd';
import {
  SaveOutlined, PlusOutlined, DeleteOutlined, MenuOutlined,
  HomeOutlined, FileTextOutlined, TeamOutlined, GlobalOutlined,
  PhoneOutlined, MailOutlined, InfoCircleOutlined, BookOutlined,
  SafetyOutlined, AuditOutlined, BankOutlined, SolutionOutlined,
  ProfileOutlined, ReadOutlined, FormOutlined, FileProtectOutlined,
  SearchOutlined, EditOutlined, FolderOutlined, BarsOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { Helmet } from 'react-helmet';

const { Title, Text } = Typography;

const ICON_OPTIONS = [
  { value: 'HomeOutlined', label: 'الرئيسية', icon: <HomeOutlined /> },
  { value: 'FileTextOutlined', label: 'نص', icon: <FileTextOutlined /> },
  { value: 'TeamOutlined', label: 'فريق', icon: <TeamOutlined /> },
  { value: 'GlobalOutlined', label: 'عالمي', icon: <GlobalOutlined /> },
  { value: 'PhoneOutlined', label: 'هاتف', icon: <PhoneOutlined /> },
  { value: 'MailOutlined', label: 'بريد', icon: <MailOutlined /> },
  { value: 'InfoCircleOutlined', label: 'معلومات', icon: <InfoCircleOutlined /> },
  { value: 'BookOutlined', label: 'كتاب', icon: <BookOutlined /> },
  { value: 'SafetyOutlined', label: 'أمان', icon: <SafetyOutlined /> },
  { value: 'AuditOutlined', label: 'تدقيق', icon: <AuditOutlined /> },
  { value: 'BankOutlined', label: 'بنك', icon: <BankOutlined /> },
  { value: 'SolutionOutlined', label: 'حلول', icon: <SolutionOutlined /> },
  { value: 'ProfileOutlined', label: 'ملف', icon: <ProfileOutlined /> },
  { value: 'ReadOutlined', label: 'قراءة', icon: <ReadOutlined /> },
  { value: 'FormOutlined', label: 'نموذج', icon: <FormOutlined /> },
  { value: 'FileProtectOutlined', label: 'حماية', icon: <FileProtectOutlined /> },
  { value: 'SearchOutlined', label: 'بحث', icon: <SearchOutlined /> },
  { value: 'EditOutlined', label: 'تعديل', icon: <EditOutlined /> },
  { value: 'FolderOutlined', label: 'مجلد', icon: <FolderOutlined /> },
  { value: 'BarsOutlined', label: 'قائمة', icon: <BarsOutlined /> },
  { value: 'LinkOutlined', label: 'رابط', icon: <LinkOutlined /> },
];

const ICON_MAP = {};
ICON_OPTIONS.forEach(opt => { ICON_MAP[opt.value] = opt.icon; });

const DEFAULT_LINKS = [
  { to: '/', label: 'الرئيسية', icon: 'HomeOutlined', enabled: true },
  { to: '/news', label: 'الأخبار', icon: 'FileTextOutlined', enabled: true },
  { to: '/reports', label: 'التقارير', icon: 'GlobalOutlined', enabled: true },
  { to: '/sections', label: 'الأقسام', icon: 'TeamOutlined', enabled: true },
  { to: '/about-us', label: 'من نحن', icon: 'InfoCircleOutlined', enabled: true },
  { to: '/contact-us', label: 'اتصل بنا', icon: 'PhoneOutlined', enabled: true },
];

const NavManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const savedLinks = data.nav_links;
        if (savedLinks) {
          try {
            const parsed = JSON.parse(savedLinks);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setLinks(parsed);
              return;
            }
          } catch (e) { /* fallback to defaults */ }
        }
      }
      setLinks(DEFAULT_LINKS);
    } catch (error) {
      message.error('تعذر جلب الإعدادات');
      setLinks(DEFAULT_LINKS);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkChange = (index, field, value) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLinks(newLinks);
  };

  const handleAddLink = () => {
    setLinks([...links, { to: '', label: '', icon: 'LinkOutlined', enabled: true }]);
  };

  const handleRemoveLink = (index) => {
    const newLinks = links.filter((_, i) => i !== index);
    setLinks(newLinks);
    message.success('تم حذف الرابط');
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newLinks = [...links];
    [newLinks[index - 1], newLinks[index]] = [newLinks[index], newLinks[index - 1]];
    setLinks(newLinks);
  };

  const handleMoveDown = (index) => {
    if (index === links.length - 1) return;
    const newLinks = [...links];
    [newLinks[index + 1], newLinks[index]] = [newLinks[index], newLinks[index + 1]];
    setLinks(newLinks);
  };

  const handleSave = async () => {
    const invalid = links.findIndex(l => !l.label.trim() || !l.to.trim());
    if (invalid !== -1) {
      message.error(`الرابط رقم ${invalid + 1} يحتوي على حقول فارغة`);
      return;
    }
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ nav_links: JSON.stringify(links) })
      });
      if (res.ok) {
        message.success('تم حفظ روابط القائمة بنجاح');
      } else {
        message.error('تعذر الحفظ');
      }
    } catch (error) {
      message.error('خطأ في الاتصال بالخادم');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>جاري تحميل الإعدادات...</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>التحكم في القائمة | لوحة التحكم</title>
      </Helmet>

      <div style={{ padding: '8px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '24px', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <Title level={2} style={{ margin: 0, color: '#1A3651' }}>
              <MenuOutlined style={{ marginLeft: '8px' }} />
              التحكم في القائمة الرئيسية
            </Title>
            <Text type="secondary">
              تعديل أسماء وأيقونات وترتيب روابط القائمة العلوية
            </Text>
          </div>
          <Space>
            <Popconfirm
              title="تأكيد الحفظ"
              description="هل أنت متأكد من حفظ التغييرات؟"
              onConfirm={handleSave}
              okText="حفظ"
              cancelText="إلغاء"
            >
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                حفظ القائمة
              </Button>
            </Popconfirm>
          </Space>
        </div>

        <Card style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {links.map((link, index) => (
              <Card
                key={index}
                size="small"
                type="inner"
                style={{
                  border: '1px solid #e8e8e8',
                  opacity: link.enabled ? 1 : 0.5,
                  transition: 'all 0.3s'
                }}
              >
                <Row gutter={[12, 12]} align="middle">
                  <Col xs={24} sm={1}>
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'
                    }}>
                      <Tooltip title={index === 0 ? 'الأول' : 'تحريك لأعلى'}>
                        <Button
                          size="small"
                          type="text"
                          icon={<span>▲</span>}
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                        />
                      </Tooltip>
                      <Text type="secondary">{index + 1}</Text>
                      <Tooltip title={index === links.length - 1 ? 'الأخير' : 'تحريك لأسفل'}>
                        <Button
                          size="small"
                          type="text"
                          icon={<span>▼</span>}
                          onClick={() => handleMoveDown(index)}
                          disabled={index === links.length - 1}
                        />
                      </Tooltip>
                    </div>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="الاسم" style={{ marginBottom: 0 }}>
                      <Input
                        value={link.label}
                        onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
                        placeholder="مثال: المقالات القانونية"
                        prefix={<EditOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={5}>
                    <Form.Item label="الرابط" style={{ marginBottom: 0 }}>
                      <Input
                        value={link.to}
                        onChange={(e) => handleLinkChange(index, 'to', e.target.value)}
                        placeholder="مثال: /articles"
                        prefix={<LinkOutlined />}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item label="الأيقونة" style={{ marginBottom: 0 }}>
                      <Select
                        value={link.icon}
                        onChange={(val) => handleLinkChange(index, 'icon', val)}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="children"
                      >
                        {ICON_OPTIONS.map(opt => (
                          <Select.Option key={opt.value} value={opt.value}>
                            <Space>
                              {opt.icon}
                              <span>{opt.label}</span>
                            </Space>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={3}>
                    <Form.Item label="ظاهر" style={{ marginBottom: 0 }}>
                      <Switch
                        checked={link.enabled}
                        onChange={(val) => handleLinkChange(index, 'enabled', val)}
                        checkedChildren="نعم"
                        unCheckedChildren="لا"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={3}>
                    <Form.Item label="&nbsp;" style={{ marginBottom: 0 }}>
                      <Popconfirm
                        title="حذف الرابط"
                        description="هل أنت متأكد؟"
                        onConfirm={() => handleRemoveLink(index)}
                        okText="حذف"
                        cancelText="إلغاء"
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          حذف
                        </Button>
                      </Popconfirm>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddLink}
              size="large"
              style={{ width: '300px' }}
            >
              إضافة رابط جديد
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
};

export default NavManager;
